/**
 * Balance profile = the *coefficients* the report says must stay replaceable by
 * data (θ_C, θ_M, λ_d, λ_R, γ_ij, tier thresholds …). Nothing here is an
 * industry standard; `SVP-0.1` is the bootstrap profile proposed in
 * deep-research-report.md. Swap it wholesale once simulation/telemetry lands.
 *
 * The report's four *structural* invariants live in the engine logic, not here,
 * because they must NOT be tuned away:
 *   1. AI interprets, the evaluator prices.
 *   2. Cooldown/cost cannot buy burst.
 *   3. CC/utility are context-dependent, not fixed damage-equivalents.
 *   4. Structurally dangerous combos are forbidden, not priced.
 */

import type { EffectKind } from './types.js';
import type { ReferenceProgressionBand } from './reference/types.js';
import { bossEhpFixture } from './calibration/skill-damage-v13.js';

/** One row of the γ (residual interaction) matrix. Matched by kind and/or tag. */
export interface InteractionRule {
  a: { kind?: EffectKind; tag?: string; aoe?: boolean };
  b: { kind?: EffectKind; tag?: string; aoe?: boolean };
  gamma: number;
  note?: string;
}


export interface ComplexityWeights {
  effect: number;
  trigger: number;
  condition: number;
  externalRef: number;
  stateWrite: number;
  complexSelector: number;
  /** coefficient on max(0, D-1)^2 where D is dependency depth. */
  depthQuad: number;
}

export interface StructuralLimits {
  /** absolute reject beyond these. */
  maxDepth: number;
  maxTriggerFanOut: number;
  maxEffectsPerActivation: number;
  /** Maximum statically expanded follow-up attacks per cast. */
  maxSpawnedAttacks: number;
  /** Maximum nesting of attacks that opt back into proc propagation. */
  maxProcGeneration: number;
  /**
   * Runtime node/event work budget; final defense against malformed input.
   * NOT_YET_STATICALLY_ENFORCEABLE from a `Skill` alone (Phase C.1 targeted
   * audit, docs/archive/gameplay/audits/phase-c1-constraint-model-audit.md §E): the
   * runtime already enforces an equivalent, unwired budget
   * (src/sim/runtime.ts `ExecutionLimits.maxEvents`/`consumeEvent()`),
   * whose realized count depends on which triggers actually fire and on
   * the caster's equipped passives (`Player.passiveDefinitions`) — data
   * with no counterpart on `Skill` and therefore no exact or sound
   * approximate static bound.
   */
  maxExecutionEvents: number;
  minTickS: number;
  maxDotDurationS: number;
  /**
   * B track (dot-as-damage, D1) — the slowest legal dot tick period, pairing
   * `minTickS`'s fastest-tick bound. Provisional/adjustable safety guard, not
   * a balance coefficient: bounds a player-pinned `tickS`, never a pricing
   * input.
   */
  maxTickS: number;
  /**
   * B track (D1) — the shortest legal dot duration, pairing
   * `maxDotDurationS`'s longest-duration bound. Provisional/adjustable.
   */
  minDurationS: number;
  /**
   * B track (D1) — the smallest legal per-tick dot amount a player may pin.
   * Provisional/adjustable safety guard against a degenerate near-zero tick.
   */
  minAmountPerTick: number;
  maxHealReductionFraction: number;
  slowCap: number;
  maxDisplacementM: number;
  maxCharges: number;
  maxChargeGain: number;
  maxCooldownRefundS: number;
  maxCleanseCount: number;
}

export interface BalanceProfile {
  id: string;

  // --- SVP normalization ---
  /**
   * Reference enemy HP per progression band; `u_HP = 100 / hpRef[band]`, so
   * **1 SVP = 1% of that band's reference enemy HP**.
   *
   * The reference enemy is the band's boss (`calibration/skill-damage-v13.ts`'s
   * `V13_BOSS_EHP_Q_REF` x `V13_BALANCED_H_REF_DAMAGE`, raw units on both sides
   * so the band's damage multipliers cancel — the same quantity
   * `sameTierBossRawDamageFraction` already reports).
   *
   * This was a single `100` from the first commit (`172e929`, 2026-08-17),
   * five days before V13 fixed boss EHP. Nothing ever reconciled them, so SVP
   * was denominated against an enemy 263x smaller than the one the progression
   * model describes, and `powerBudget: 20` meant "a whole skill may be worth
   * 20 damage" = 12% of one Lv60 basic attack. `skill-value-engine-contract.md`
   * §28.1 had already marked these bootstrap numbers superseded.
   */
  hpRef: Readonly<Record<ReferenceProgressionBand, number>>;

  // --- evaluation windows ---
  H: number; // sustained horizon (s)
  tau: number; // burst window (s)

  // --- coefficients ---
  lambdaD: number; // Non-damage time-discount rate (1/s)
  thetaC: number; // SVP per full action-denied second
  thetaM: number; // SVP per critical-movement second
  thetaP: number; // SVP per unit positional-potential (knockback/pull)
  lambdaMana: number; // SVP per mana (shadow price)
  lambdaEnergy: number; // SVP per energy before scarcity adjustment
  resourceScarcityFloor: number; // min multiplier on renewable-resource price
  resourceScarcityCap: number; // max multiplier on renewable-resource price
  hpCostBase: number; // λ_HP(h) = hpCostBase * h^(-hpCostExp)
  hpCostExp: number;
  qMove: number; // fraction of buff time that is critical movement
  alpha: number; // robust-price blend: (1-α)·mean + α·Q90
  eta: number; // interaction clip: |I| ≤ η·V_base

  complexity: ComplexityWeights;
  interactions: InteractionRule[];
  limits: StructuralLimits;
}

export const SVP_0_1: BalanceProfile = {
  id: 'svp-0.1',
  hpRef: Object.freeze({
    novice: bossEhpFixture('novice'),
    intermediate: bossEhpFixture('intermediate'),
    advanced: bossEhpFixture('advanced'),
  }),
  H: 30,
  tau: 2,
  lambdaD: 0.1,
  // Re-anchored together with `hpRef`. Every coefficient below is an ABSOLUTE
  // SVP price, so none of them moved when `hpRef` did — they were all set in
  // the `hpRef: 100` world and had to be re-derived, not rescaled. Rescaling
  // would have preserved the old ratio, and the old ratio priced a full second
  // of action denial at 0.56% of one reference skill: effectively free.
  //
  // The anchor used instead is fight time. Killing a band's boss is 100 SVP by
  // definition; a matched boss fight runs ~26-95s once skills are in the
  // rotation, so one second of it is worth 100/95 .. 100/26 = 1.05 .. 3.8 SVP.
  // thetaC sits mid-range at 2, which puts a 1s hard CC at about half of one
  // reference skill's budget — "1s stun plus half damage" is buildable in a
  // single skill, which it was not at 6.
  thetaC: 2, // full action denial, per second
  thetaM: 1, // critical-movement denial, per second — worth less than full denial
  thetaP: 1, // per unit of knockback/pull positional potential
  // A full resource bar is worth roughly one reference skill (~4 SVP) and the
  // band mana pools are 50/90/150, so ~0.05 SVP per point.
  lambdaMana: 0.05,
  lambdaEnergy: 0.05,
  resourceScarcityFloor: 0.5,
  resourceScarcityCap: 4,
  hpCostBase: 0.5,
  hpCostExp: 1.5,
  qMove: 0.6,
  alpha: 0.25,
  eta: 0.25,

  complexity: {
    effect: 1.0,
    trigger: 1.5,
    condition: 1.0,
    externalRef: 1.25,
    stateWrite: 1.0,
    complexSelector: 0.5,
    depthQuad: 0.75,
  },

  // Seeded sparse; ultimately learned from counterfactual simulation.
  interactions: [
    { a: { kind: 'dot' }, b: { kind: 'heal_reduction' }, gamma: 0.1, note: 'burn × heal-reduction' },
    { a: { kind: 'damage', aoe: true }, b: { kind: 'stun' }, gamma: 0.08, note: 'AoE burst + stun' },
    { a: { kind: 'shield' }, b: { kind: 'heal' }, gamma: -0.05, note: 'overlapping survivability' },
    { a: { kind: 'slow' }, b: { kind: 'dot' }, gamma: 0.12, note: 'slow × ground DoT' },
    { a: { kind: 'stun' }, b: { kind: 'dot' }, gamma: 0.15, note: 'stun × delayed AoE' },
  ],

  limits: {
    maxDepth: 3,
    maxTriggerFanOut: 4,
    maxEffectsPerActivation: 16,
    maxSpawnedAttacks: 32,
    maxProcGeneration: 3,
    maxExecutionEvents: 64,
    minTickS: 0.25,
    maxDotDurationS: 30,
    maxTickS: 5,
    minDurationS: 0.5,
    minAmountPerTick: 0.01,
    maxHealReductionFraction: 0.8,
    slowCap: 0.9,
    maxDisplacementM: 12,
    maxCharges: 8,
    maxChargeGain: 3,
    maxCooldownRefundS: 30,
    maxCleanseCount: 5,
  },
};
